import angular from 'angular'

import { bindAll, isFunction } from 'lodash-es'

import setCss from '../utils/set-css'
import browser from '../utils/browser'
import extractRawElm from '../utils/extract-raw-elm'

angular
  .module('ggs.html-player')
  .factory('HtmlPlayer', HtmlPlayerProvider)
  .factory('HtmlPlayerInternal', HtmlPlayerInternalProvider)

function importPlyrChunk(chunkName) {
  return import(
    /* https://webpack.js.org/api/module-methods/#magic-comments */
    /* webpackMode: "lazy-once" */
    /* webpackInclude: /plyr\.(js|css)$/ */
    /* webpackChunkName: "gallery-v2.plyr" */
    `plyr/dist/${chunkName}`)
}

const METHODS = ['play', 'pause', 'autoplay', 'destroy']

const AUDIO_TYPE = 'audio'
const VIDEO_TYPE = 'video'

const CONTROLS_CSS = { 'z-index': 2 }
const CONTAINER_CSS = { 'min-width': 'initial', 'min-height': 'initial' }

const AUDIO_MEDIA_CSS = { position: 'absolute' }
const AUDIO_POSTER_CSS = { position: 'static' }

const MEDIA_INIT_CSS = { visibility: 'hidden' }
const MEDIA_SHOW_CSS = { visibility: null }

const POSTER_INIT_CSS = { opacity: 1, transition: 'opacity 0.3s' }
const POSTER_HIDE_CSS = { opacity: 0 }

const READY_EVENT = browser.Android && browser.Gecko ? 'ready' : 'loadedmetadata'

const TIMEOUT_DELAY = 10e3
const TIMEOUT_SKIP_APPLY = false

function HtmlPlayerInternalProvider(
  qh,
  $timeout,
  $rootScope,
  applyAsync) {

  return function HtmlPlayerInternal(
    Plyr,
    $elm,
    setup,
    media,
    events,
    sourceStyleGetter,
    posterStyleGetter,
    sourceStyleEmitter,
    posterStyleEmitter) {

    const elm = extractRawElm($elm)
    const videoElm = createVideo(media)
    elm.appendChild(videoElm)
    const plyr = createPlayer(videoElm, setup)

    plyr.on(READY_EVENT, readyHandler)
    plyr.on('canplay', canplayHandler)

    plyr.on(READY_EVENT, styleHandler)
    plyr.on('enterfullscreen', styleHandler)
    plyr.on('exitfullscreen', styleHandler)

    sourceStyleEmitter.on(styleHandler)
    posterStyleEmitter.on(styleHandler)

    const timeoutId = $timeout(
      errorHandler,
      TIMEOUT_DELAY,
      TIMEOUT_SKIP_APPLY)

    const htmlPlayer = {
      play,
      pause,
      autoplay,
      destroy
    }

    return htmlPlayer

    function play() {
      return qh.try(plyr.play)
    }

    function pause() {
      return qh.try(plyr.pause)
    }

    function autoplay() {
      return qh.try(plyr.play).then(() => {
        if (media.type === VIDEO_TYPE) {
          setCss(plyr.elements.controls, 'display', 'none')
          plyr.elements.container.classList.add(plyr.config.classNames.hideControls)
        }
      }).then(qh.makeDelay(33)).then(() => {
        if (media.type === VIDEO_TYPE) {
          setCss(plyr.elements.controls, 'display', null)
        }
      })
    }

    function destroy() {
      $timeout.cancel(timeoutId)
      plyr.off(READY_EVENT, readyHandler)
      plyr.off(READY_EVENT, styleHandler)
      plyr.off('canplay', canplayHandler)
      plyr.off('enterfullscreen', styleHandler)
      plyr.off('exitfullscreen', styleHandler)
      sourceStyleEmitter.off(styleHandler)
      posterStyleEmitter.off(styleHandler)
      plyr.destroy()
    }

    function styleHandler() {
      setCss(plyr.elements.poster, posterStyleGetter())
      if (media.type === VIDEO_TYPE) {
        setCss(plyr.media, sourceStyleGetter())
      }
    }

    function readyHandler() {
      $timeout.cancel(timeoutId)
      plyr.off(READY_EVENT, readyHandler)
      setCss(plyr.media, MEDIA_SHOW_CSS)

      if (isFunction(events.ready)) {
        const width = plyr.media.videoWidth
        const height = plyr.media.videoHeight
        const hasSize = (media.type === VIDEO_TYPE) && (width > 0) && (height > 0)
        const size = hasSize ? { width, height } : null
        applyAsync($rootScope, events.ready, htmlPlayer, size)
      }
    }

    function canplayHandler() {
      plyr.off('canplay', canplayHandler)

      if (media.type === VIDEO_TYPE) {
        setCss(plyr.elements.poster, POSTER_HIDE_CSS)
      }
    }

    function errorHandler(error) {
      $timeout.cancel(timeoutId)
      plyr.off(READY_EVENT, readyHandler)

      if (isFunction(events.error)) {
        applyAsync($rootScope, events.error, htmlPlayer, error)
      }
    }

    function createPlayer(videoElm, setup) {
      const plyr = new Plyr(videoElm, setup)
      bindAll(plyr, 'play', 'pause')

      const newPoster = document.createElement('img')
      const oldPoster = plyr.elements.poster
      newPoster.src = media.poster
      newPoster.className = oldPoster.className
      oldPoster.parentNode.insertBefore(newPoster, oldPoster)
      oldPoster.parentNode.removeChild(oldPoster)
      plyr.elements.poster = newPoster

      setCss(plyr.media, MEDIA_INIT_CSS)
      setCss(plyr.elements.poster, POSTER_INIT_CSS)
      setCss(plyr.elements.controls, CONTROLS_CSS)
      setCss(plyr.elements.container, CONTAINER_CSS)

      if (media.type === AUDIO_TYPE) {
        setCss(plyr.media, AUDIO_MEDIA_CSS)
        setCss(plyr.elements.poster, AUDIO_POSTER_CSS)
      }

      return plyr
    }

    function createVideo(media) {
      const videoElm = document.createElement('video')
      videoElm.setAttribute('playsinline', 'true')

      if (browser.Android && browser.Gecko && media.type === VIDEO_TYPE) {
        videoElm.setAttribute('poster', media.poster)
      }

      for (const source of media.sources) {
        const sourceElm = document.createElement('source')
        sourceElm.setAttribute('src', source.src)
        sourceElm.setAttribute('type', source.type)
        videoElm.appendChild(sourceElm)
      }

      return videoElm
    }
  }
}

function HtmlPlayerProvider(
  qh,
  $log,
  $rootScope,
  applyAsync,
  LazyResource,
  HtmlPlayerInternal) {

  return function HtmlPlayer(
    $elm,
    setup,
    media,
    events,
    sourceStyleGetter,
    posterStyleGetter,
    sourceStyleEmitter,
    posterStyleEmitter) {

    const lazyHttpPlayer = LazyResource(METHODS)

    qh.join(importPlyrChunk('plyr.js'), importPlyrChunk('plyr.css')).then(([{ default: Plyr }]) => {
      const htmlPlayer = HtmlPlayerInternal(
        Plyr,
        $elm,
        setup,
        media,
        events,
        sourceStyleGetter,
        posterStyleGetter,
        sourceStyleEmitter,
        posterStyleEmitter)

      for (const methodName of METHODS) {
        lazyHttpPlayer[methodName].allow(htmlPlayer[methodName])
      }
    }, (err) => {
      $log.warn('HtmlPlayer: plyr cannot be loaded due to ', err)
      if (events.error) {
        applyAsync($rootScope, events.error, lazyHttpPlayer, err)
      }
    })

    return lazyHttpPlayer
  }
}
